import { useEffect, useState } from "react";
import { Box, Button, ButtonBase, Typography, Paper, Divider } from "@mui/material";
import { Category } from "../../../../types/models/Category";
import BeaconCategory from "../../../../components/BeaconCategory";
import { useAppSelector } from "../../../../utils/hooks";
import { Settings } from "../../../../types/models/Settings";
import { FormikErrors, FormikTouched } from "formik";
import entertainmentImage from './icons/entertainment.png';
import fooddrinkImage from './icons/fooddrink.png';
import serviceImage from './icons/service.png';
import locationImage from './icons/location.png';

type CategoryListProps = {
    values: Settings,
    errors: FormikErrors<Settings>,
    touched: FormikTouched<Settings>,
    handleCategorySelect: (category: Category | null) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Settings>>,
    experienceMode: string
};

const CategoriesList = ({ handleCategorySelect, values, errors, touched, setFieldValue, experienceMode }: CategoryListProps) => {
    const group = useAppSelector(state => state.businessGroup.currentBusinessGroup);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [categoryIndex, setCategoryIndex] = useState(0);
    const [defaultCategoriesAdded, setDefaultCategoriesAdded] = useState(false);

    useEffect(() => {
        if ((values.categories ?? []).length === 0 && experienceMode === "CREATE" && !defaultCategoriesAdded) {
            addDefaultCategories();
        }
    }, [values.categories, experienceMode]);

    const handleSelect = (category: Category) => {
        setSelectedCategory(category);
        handleCategorySelect(category);
    };

    const addDefaultCategories = async () => {
        const categoryList = [...(values.categories ?? [])];

        const defaultCategories = [
            { name: "Food & Drink", color: "#cc3333", image: fooddrinkImage},
            { name: "Entertainment", color: "#3399cc", image: entertainmentImage},
            { name: "Service", color: "#ff6633", image: serviceImage}
        ];

        try {
            const newCategories = await Promise.all(
                defaultCategories.map(async (categoryInfo, index) => {
                    const response = await fetch(categoryInfo.image);
                    const blob = await response.blob();
                    const file = new File([blob], `${categoryInfo.name}.png`, { type: blob.type });

                    return {
                        Id: (categoryIndex + index).toString(),
                        Name: categoryInfo.name,
                        Color: categoryInfo.color,
                        Theme: "ball",
                        BeaconHeight: "High",
                        ShowDistance: true,
                        MaxDistanceVisible: undefined,
                        MinDistanceVisible: undefined,
                        Description: "",
                        HasBeaconHeadIcon: false,
                        LastModified: "",
                        currentFilterFile: file,
                        Index: (categoryIndex + index).toString()
                    };
                })
            );

            setCategoryIndex((prev) => prev + defaultCategories.length);
            setFieldValue("categories", [...categoryList, ...newCategories]);

            setDefaultCategoriesAdded(true);
        } catch (error) {
            console.error("Error loading default category icon", error);
        }
    };

    const handleAddCategory = async () => {

        const updatedCategories = [...(values.categories ?? [])];

        const response = await fetch(locationImage);
        const blob = await response.blob();
        const file = new File([blob], 'location.png', { type: blob.type });

        const newCategory: Category = {
            Id: categoryIndex.toString(),
            Name: "",
            Color: "#D0CCCC",
            Theme: "ball",
            BeaconHeight: "High",
            ShowDistance: true,
            MaxDistanceVisible: undefined,
            MinDistanceVisible: undefined,
            Description: "",
            HasBeaconHeadIcon: false,
            LastModified: "",
            currentFilterFile: file,
            BeaconGroupId: group?.id,
            Index: categoryIndex.toString()
        };
    
        // Add the new category to the categories array
        updatedCategories.push(newCategory);
        setCategoryIndex((prev) => prev + 1);

        // Update the form state with the new categories array
        setFieldValue("categories", updatedCategories);
    
        // Optionally, set the newly added category as the selected category
        setSelectedCategory(newCategory);
        handleCategorySelect(newCategory);
    };

    return (
        <Paper sx={{ border: '1px solid #ccc', backgroundColor: "white", borderRadius: '4px', padding: '16px', marginTop: '16px', maxWidth: "235px", height: "fit-content" }}>
            {(values.categories ?? []).map((category) => {
                const isSelected = selectedCategory?.Id === category.Id;
                return (
                    <Box key={category.Id} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginTop: 2 }}>
                        <ButtonBase
                            sx={{ borderRadius: "22px" }}
                            onClick={() => handleSelect(category)}
                        >
                            <BeaconCategory
                                category={category.Name}
                                id={category.Id ?? ""}
                                fontSize="14px"
                                minWidth="200px"
                                borderRadius="22px"
                                padding="4px 8px"
                                justifyContent="left"
                                icons={true}
                                selected={isSelected}
                                hover={true}
                                lastModfied={category.LastModified}
                                color={category.Color}
                                localImage={category.currentFilterFile}
                                error={touched.categories && Boolean(errors.categories) && category.Name.length < 1}
                            />
                        </ButtonBase>
                    </Box>
                );
            })}
            <Divider sx={{ marginTop: 3 }} />
            {experienceMode !== "READ_ONLY" && (
            <Box mt={2}>
                <Button sx={{ width: "200px", display: "flex", justifyContent: "flex-start" }} onClick={handleAddCategory}>
                    <Typography fontSize={"14px"} fontWeight={"700"} color={"black"}>+ Add Category</Typography>
                </Button>
            </Box>
            )}
        </Paper>
    );
};

export default CategoriesList;
